import React from "react";
import { gql, useQuery } from "@apollo/client";
import { withStyles } from "@material-ui/core/styles";
import TablePagination from "@material-ui/core/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
  Typography
} from "@material-ui/core";
import TableLink from "../TableLink";
import {
  getRecordTitleFromSort,
  setUrlFromFilterAndSort,
  assembleTeamPerformanceFilter,
  queryStringToFilterState
} from "../../utils/filters";
import Filters from "../Filters";

const styles = theme => ({
  root: {
    marginTop: theme.spacing(2),
    overflowX: "auto",
    margin: "auto"
  },
  table: {
    minWidth: 700
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 300
  }
});

const GET_SCHOOL_TOTALS = gql`
  query schoolTotalQuery(
    $first: Int
    $offset: Int
    $orderBy: [_SchoolTotalOrdering]
    $filter: _TeamPerformanceFilter
  ) {
    SchoolTotal(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: $filter
    ) {
      id
      short_name
      powers
      points
      gets
      negs
      ppb
      tournaments_played
      teams_entered
      games_played
      wins
      losses
      winning_percentage
    }
  }
`;

function SchoolTotal(props) {
  const { classes } = props;
  const [order, setOrder] = React.useState(
    new URLSearchParams(props.location.search).get("order") || "desc"
  );
  const [orderBy, setOrderBy] = React.useState(
    new URLSearchParams(props.location.search).get("orderBy") || "points"
  );
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filterState, setFilterState] = React.useState(
    queryStringToFilterState(props.location.search)
  );

  const getFilter = () => {
    let filter = assembleTeamPerformanceFilter(filterState);

    return filter;
  };

  const { loading, data, error } = useQuery(GET_SCHOOL_TOTALS, {
    variables: {
      first: rowsPerPage,
      offset: rowsPerPage * page,
      orderBy: `${orderBy}_${order}`,
      filter: getFilter()
    }
  });

  const handleChangePage = (_, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (_, rows) => {
    setRowsPerPage(parseInt(rows.key));
  };

  const handleSortRequest = property => {
    const newOrderBy = property;
    let newOrder = "desc";

    if (orderBy === property && order === "desc") {
      newOrder = "asc";
    }

    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setUrlFromFilterAndSort(filterState, {
      order: newOrder,
      orderBy: newOrderBy
    });
  };

  const handleFiltersUpdate = filterState => {
    setUrlFromFilterAndSort(filterState, { order, orderBy });
    setFilterState(filterState);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        {getRecordTitleFromSort("", orderBy, order)}
      </Typography>
      <Typography variant="caption" display="block">
        Sort or filter to view other school records
      </Typography>
      <Filters
        filters={filterState}
        onFiltersUpdate={handleFiltersUpdate}
        hideWinLoss
      ></Filters>
      {loading && !error && <p>Loading...</p>}
      {error && !loading && <p>Error</p>}
      {data && !loading && !error && (
        <div>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell key="name">Name</TableCell>
                <TableCell
                  key="powers"
                  sortDirection={orderBy === "powers" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "powers"}
                      direction={order}
                      onClick={() => handleSortRequest("powers")}
                    >
                      Powers
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="gets"
                  sortDirection={orderBy === "gets" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "gets"}
                      direction={order}
                      onClick={() => handleSortRequest("gets")}
                    >
                      Gets
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="negs"
                  sortDirection={orderBy === "negs" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "negs"}
                      direction={order}
                      onClick={() => handleSortRequest("negs")}
                    >
                      Negs
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="points"
                  sortDirection={orderBy === "points" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "points"}
                      direction={order}
                      onClick={() => handleSortRequest("points")}
                    >
                      Points
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="ppb"
                  sortDirection={orderBy === "ppb" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "ppb"}
                      direction={order}
                      onClick={() => handleSortRequest("ppb")}
                    >
                      PPB
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="tournaments_played"
                  sortDirection={
                    orderBy === "tournaments_played" ? order : false
                  }
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "tournaments_played"}
                      direction={order}
                      onClick={() => handleSortRequest("tournaments_played")}
                    >
                      Tournaments Played
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="teams_entered"
                  sortDirection={orderBy === "teams_entered" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "teams_entered"}
                      direction={order}
                      onClick={() => handleSortRequest("teams_entered")}
                    >
                      Teams Entered
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="games_played"
                  sortDirection={orderBy === "games_played" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "games_played"}
                      direction={order}
                      onClick={() => handleSortRequest("games_played")}
                    >
                      Games Played
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="wins"
                  sortDirection={orderBy === "wins" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "wins"}
                      direction={order}
                      onClick={() => handleSortRequest("wins")}
                    >
                      Wins
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="losses"
                  sortDirection={orderBy === "losses" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "losses"}
                      direction={order}
                      onClick={() => handleSortRequest("losses")}
                    >
                      Losses
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="winning_percentage"
                  sortDirection={
                    orderBy === "winning_percentage" ? order : false
                  }
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "winning_percentage"}
                      direction={order}
                      onClick={() => handleSortRequest("winning_percentage")}
                    >
                      W%
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.SchoolTotal.map(n => {
                return (
                  <TableRow key={n.id}>
                    <TableCell component="th" scope="row">
                      <TableLink to={`/school/${n.id}`}>
                        {n.short_name}
                      </TableLink>
                    </TableCell>
                    <TableCell>{n.powers?.toLocaleString()}</TableCell>
                    <TableCell>{n.gets?.toLocaleString()}</TableCell>
                    <TableCell>{n.negs?.toLocaleString()}</TableCell>
                    <TableCell>{n.points?.toLocaleString()}</TableCell>
                    <TableCell>{n.ppb?.toFixed(2)}</TableCell>
                    <TableCell>
                      {n.tournaments_played?.toLocaleString()}
                    </TableCell>
                    <TableCell>{n.teams_entered?.toLocaleString()}</TableCell>
                    <TableCell>{n.games_played?.toLocaleString()}</TableCell>
                    <TableCell>{n.wins?.toLocaleString()}</TableCell>
                    <TableCell>{n.losses?.toLocaleString()}</TableCell>
                    <TableCell>{n.winning_percentage?.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={Number.MAX_VALUE}
            labelDisplayedRows={({ from, to }) => `Results ${from} - ${to}`}
            nextIconButtonProps={
              data.SchoolTotal.length < rowsPerPage ? { disabled: true } : {}
            }
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      )}
    </div>
  );
}

export default withStyles(styles)(SchoolTotal);
